import axios, { AxiosError } from 'axios'
import type { AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios'

import { env } from '@/config/env'
import { tokenStorage } from '@/lib/storage/tokenStorage'
import type { AuthTokens } from '@/types/auth'

type RefreshResponse = {
  accessToken: string
  refreshToken: string
  expiresIn: number
  refreshExpiresIn?: number
}

const BASE_URL = env.apiBaseUrl

const createApiClient = () => {
  const instance = axios.create({
    baseURL: BASE_URL,
    withCredentials: true,
  })

  instance.interceptors.request.use((config) => {
    const token = tokenStorage.getAccessToken()
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }

    return config
  })

  attachResponseInterceptor(instance)

  return instance
}

let apiClient: AxiosInstance | null = null

const refreshClient = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
})

let refreshPromise: Promise<AuthTokens | null> | null = null

const refreshTokens = async (): Promise<AuthTokens | null> => {
  const storedTokens = tokenStorage.get()

  if (!storedTokens?.refreshToken) {
    return null
  }

  // Since backend uses single JWT (no separate refresh endpoint),
  // check if the JWT itself is still valid by parsing its expiry.
  try {
    const jwt = storedTokens.refreshToken
    const payloadStr = atob(jwt.split('.')[1])
    const payload = JSON.parse(payloadStr) as { exp: number }
    const nowSec = Math.floor(Date.now() / 1000)

    if (payload.exp && payload.exp > nowSec) {
      // JWT is still valid — re-use it with updated frontend expiry
      const refreshedTokens: AuthTokens = {
        accessToken: jwt,
        refreshToken: jwt,
        expiresAt: payload.exp * 1000, // Use actual JWT expiry
        refreshExpiresAt: payload.exp * 1000,
      }
      tokenStorage.set(refreshedTokens)
      return refreshedTokens
    }

    // JWT has truly expired — user needs to re-login
    tokenStorage.clear()
    return null
  } catch {
    // Fallback: try the refresh endpoint in case backend is updated later
    try {
      const response = await refreshClient.post<RefreshResponse>('/auth/refresh', {
        refreshToken: storedTokens.refreshToken,
      })

      const refreshedTokens: AuthTokens = {
        accessToken: response.data.accessToken,
        refreshToken: response.data.refreshToken,
        expiresAt: Date.now() + response.data.expiresIn * 1000,
        refreshExpiresAt: response.data.refreshExpiresIn
          ? Date.now() + response.data.refreshExpiresIn * 1000
          : undefined,
      }

      tokenStorage.set(refreshedTokens)
      return refreshedTokens
    } catch {
      tokenStorage.clear()
      return null
    }
  }
}

const getRefreshPromise = () => {
  if (!refreshPromise) {
    refreshPromise = refreshTokens().finally(() => {
      refreshPromise = null
    })
  }

  return refreshPromise
}

const attachResponseInterceptor = (instance: AxiosInstance) => {
  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & {
        _retry?: boolean
      }

      const status = error.response?.status

      if (status !== 401 || !originalRequest || originalRequest._retry) {
        return Promise.reject(error)
      }

      originalRequest._retry = true

      const tokens = await getRefreshPromise()

      if (!tokens) {
        return Promise.reject(error)
      }

      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${tokens.accessToken}`
      }

      return instance(originalRequest as AxiosRequestConfig)
    },
  )
}

export const getApiClient = () => {
  if (!apiClient) {
    apiClient = createApiClient()
  }

  return apiClient
}

export const resetApiClient = () => {
  apiClient = null
}

export const refreshTokensManually = () => refreshTokens()
